import * as React from "react";
import executeScriptElements from "../../utils/executeScriptElements";
import useAsyncState from "../../hooks/useAsyncState";
// import regex from "../../constants/Regex";
import SectionHeader from "../molecules/SectionHeader";
import Loader from "../atoms/Loader";
import ServerErrorMessage from "../atoms/ServerErrorMessage";

import API, { getUserNonce } from "../../api";

type PropsType = {
    title?: string;
    description?: string;
    embedded_code: string;
};

const ua = window.navigator.userAgent;
const maybeSafari = /iP(ad|od|hone)/i.test(ua) || /Mac OS X/i.test(ua);

function addValidationToDateTimePair(
    startDateField: HTMLInputElement,
    endDateField: HTMLInputElement
) {
    startDateField.addEventListener("change", e => {
        endDateField.min = startDateField.value;
    });
    endDateField.addEventListener("invalid", e => {
        if (endDateField.validity.rangeUnderflow) {
            endDateField.setCustomValidity(
                "Event End Time needs to be after Start Time."
            );
        }
    });
}

const MarketingForm = (props: PropsType) => {
    const [state, localActions] = useAsyncState();
    const scriptRef = React.useRef<HTMLDivElement>(null);

    const prepareFileUploadFields = (
        field: HTMLInputElement | HTMLTextAreaElement
    ) => {
        const fieldLabel = field.parentNode?.querySelector("label");
        if (fieldLabel && fieldLabel.textContent?.includes("[file]")) {
            // field is a file upload

            // remove [file] text from input label
            fieldLabel.innerHTML = fieldLabel.textContent.replace("[file]", "");

            // set files being uploaded flag on form container
            scriptRef.current?.setAttribute("data-files-uploading", "true");

            // add parent class
            (field.parentNode as Element)?.classList.add("lp-form-field--file");

            // hide the real text input
            field.style.display = "none";

            // create and render upload input
            const fileUploadInput = document.createElement("input");
            fileUploadInput.type = "file";
            fileUploadInput.classList.add("lp-form-fieldInput");
            fileUploadInput.setAttribute("data-file-field", field.name);

            const fieldRequiredAttribute = field.getAttribute("required");
            if (fieldRequiredAttribute) {
                fileUploadInput.setAttribute(
                    "required",
                    fieldRequiredAttribute
                );
            }

            field.parentNode?.appendChild(fileUploadInput);

            field.value = "temp-file-upload";

            fileUploadInput.onchange = handleFileUploadChange;
        }
    };

    const handleFileUploadChange = (e: any) => {
        const fileField = e.target;
        const errors: string[] = [];

        // remove old error
        if (fileField.parentNode.querySelector(".react-form__field__errors")) {
            fileField.parentNode
                .querySelector(".react-form__field__errors")
                .remove();
        }
        fileField.removeAttribute("aria-invalid");

        // check for new errors
        if (fileField && fileField.files[0]) {
            // file type validation
            const allowedFiles = [
                ".pdf",
                ".jpeg",
                ".jpg",
                ".png",
                ".apng",
                ".doc",
                ".docx",
                ".xls",
                ".xlsx",
                ".heic",
            ];
            const formatTypes = allowedFiles.join("|");
            const fileTypeRegex = new RegExp(formatTypes, "i");
            if (!fileTypeRegex.exec(fileField.files[0].name)) {
                errors.push("The file format is not allowed. ");
            }

            // file size validation
            if (fileField.files[0].size > 5120 * 1000) {
                errors.push("Please only upload files under 5120kb.");
            }

            if (errors.length) {
                const errorElement = document.createElement("div");
                errorElement.classList.add("react-form__field__errors");
                errorElement.innerText = errors.join(" ");

                fileField.parentNode.appendChild(errorElement);
                fileField.setAttribute("aria-invalid", true);
            }
        }
    };

    const handleFormLoaded = () => {
        // remove any attached stylesheets
        scriptRef.current
            ?.querySelectorAll('style,link[rel="stylesheet"]')
            .forEach(item => item.remove());

        const inputList = scriptRef.current?.querySelectorAll("input");
        const textareaList = scriptRef.current?.querySelectorAll("textarea");

        const marketingFields = [
            ...(inputList ? Array.from(inputList) : []),
            ...(textareaList ? Array.from(textareaList) : []),
        ];

        if (marketingFields) {
            marketingFields.forEach((field, i) => {
                const fieldParent = field.parentNode as Element;

                if (field.type === "checkbox" || field.type === "radio") {
                    const labelDiv = document.createElement("div");
                    labelDiv.classList.add(`lp-${field.type}-indicator`);

                    if (fieldParent.classList.contains("lp-form-field")) {
                        fieldParent.classList.add(`lp-${field.type}Container`);
                    } else {
                        (
                            fieldParent?.parentNode?.parentNode as Element
                        )?.classList.add(`lp-${field.type}Container`);
                    }

                    fieldParent
                        .querySelectorAll("label")[0]
                        ?.appendChild(labelDiv);
                }

                if (field.type === "date" || field.type === "datetime-local") {
                    fieldParent.classList.add("lp-dateContainer");
                }

                if (field.getAttribute("placeholder")) {
                    fieldParent.classList.add("has-placeholder");
                }

                field.addEventListener("focus", () => {
                    if (fieldParent.classList.contains("lp-form-field")) {
                        fieldParent.classList.add("is-focused");
                    } else {
                        (fieldParent.parentNode as Element)?.classList.add(
                            "is-focused"
                        );
                    }
                });

                field.addEventListener("blur", () => {
                    if (fieldParent.classList.contains("lp-form-field")) {
                        fieldParent.classList.remove("is-focused");
                    } else {
                        (fieldParent.parentNode as Element)?.classList.remove(
                            "is-focused"
                        );

                        if (field.getAttribute("data-value")) {
                            (fieldParent.parentNode as Element)?.classList.add(
                                "has-value"
                            );
                        } else {
                            (
                                fieldParent.parentNode as Element
                            )?.classList.remove("has-value");
                        }
                    }
                });

                if (
                    field instanceof HTMLInputElement &&
                    field.type === "radio"
                ) {
                    field.addEventListener("click", e => {
                        if (fieldParent.classList.contains("has-value")) {
                            fieldParent.classList.remove("has-value");
                            field.checked = !field.checked;
                        }

                        if (field.checked) {
                            (fieldParent.parentNode as Element)
                                ?.querySelectorAll(".lp-radioButton")
                                .forEach((radio, i) => {
                                    radio.classList.remove("has-value");
                                    const radioInput =
                                        radio.querySelectorAll("input")[0];
                                    if (radioInput) {
                                        radioInput.checked = false;
                                    }
                                });
                            fieldParent.classList.add("has-value");
                            field.checked = true;
                        }
                    });
                } else {
                    field.addEventListener("input", () => {
                        if (
                            field instanceof HTMLInputElement &&
                            field.type === "checkbox"
                        ) {
                            if (field.checked) {
                                fieldParent.classList.add("has-value");
                            } else {
                                fieldParent.classList.remove("has-value");
                            }
                        } else {
                            if (
                                fieldParent.classList.contains("lp-form-field")
                            ) {
                                if (field.value.length > 0) {
                                    (
                                        fieldParent.parentNode as Element
                                    )?.classList.add("has-value");
                                } else {
                                    (
                                        fieldParent.parentNode as Element
                                    )?.classList.remove("has-value");
                                }
                            } else {
                                if (field.value.length > 0) {
                                    fieldParent.classList.add("has-value");
                                } else {
                                    fieldParent.classList.remove("has-value");
                                }
                            }
                        }
                    });
                }

                prepareFileUploadFields(field);
            });
        }

        const marketingTextArea =
            scriptRef.current?.querySelectorAll("textarea");

        if (marketingTextArea) {
            marketingTextArea.forEach((field, i) => {
                (field.parentNode?.parentNode as Element)?.classList.add(
                    "lp-textAreaContainer"
                );
            });
        }

        const marketingSelect = scriptRef.current?.querySelectorAll("select");

        if (marketingSelect) {
            marketingSelect.forEach((field, i) => {
                (field.parentNode as Element)?.classList.add(
                    "lp-selectContainer"
                );

                if (field.firstElementChild != null) {
                    field.firstElementChild.innerHTML = "Select";
                }

                field.addEventListener("change", () => {
                    if (field.value.length > 0) {
                        (field.parentNode as Element)?.classList.add(
                            "has-value"
                        );
                    } else {
                        (field.parentNode as Element)?.classList.remove(
                            "has-value"
                        );
                    }
                });
            });
        }

        const startDateField = scriptRef.current?.querySelector(
            "input[name='b34738b6-732d-ec11-b6e6-00224810a7b2']"
        );
        const endDateField = scriptRef.current?.querySelector(
            "input[name='d1a8a3ce-732d-ec11-b6e6-00224810a7b2']"
        );

        if (
            !maybeSafari &&
            startDateField instanceof HTMLInputElement &&
            endDateField instanceof HTMLInputElement
        ) {
            addValidationToDateTimePair(startDateField, endDateField);
        }
    };

    const handleFormSubmit = (e: any) => {
        const filesNeedingUpload = scriptRef.current?.getAttribute(
            "data-files-uploading"
        );
        const errorFields = scriptRef.current?.querySelector(
            '[aria-invalid="true"]'
        );

        if (filesNeedingUpload) {
            e.preventDefault();

            if (errorFields) {
                // halt form submission if errors still
                return false;
            }

            localActions.request(
                <>
                    <p>Please wait while we process your submission.</p>
                    <p>Do not refresh the page.</p>
                </>
            );

            // handle file uploads
            handleFileUploads()
                ?.then(() => {
                    localActions.success();

                    scriptRef.current?.removeAttribute("data-files-uploading");

                    (
                        scriptRef.current?.querySelector(
                            `button[type="submit"]`
                        ) as HTMLButtonElement | null
                    )?.click();
                })
                .catch(error => {
                    localActions.fail(
                        error.message || "There was an issue uploading files."
                    );
                });
        }
    };

    const handleFileUploads = () => {
        if (scriptRef.current) {
            const fileUploadFields = scriptRef.current.querySelectorAll(
                'input[type="file"]'
            ) as NodeListOf<HTMLInputElement>;

            if (fileUploadFields && fileUploadFields.length) {
                // show file upload loader
                // localActions.request(
                //     <>
                //         <p>Please wait while we process your submission.</p>
                //         <p>Do not refresh the page.</p>
                //     </>
                // );

                // collect upload promise for each found file input in form
                return Promise.all(
                    Array.from(fileUploadFields).map(async fileField => {
                        if (
                            fileField.files?.length &&
                            fileField.files?.length > 0
                        ) {
                            const file = fileField.files[0];
                            const fileLabel = (
                                fileField.parentNode as Element
                            ).querySelector("label");
                            const filePrefix = fileLabel
                                ? fileLabel.textContent ?? ""
                                : "";

                            if (file) {
                                const imageUploadForm = new FormData();
                                imageUploadForm.append("file", file);
                                imageUploadForm.append(
                                    "webform-file-upload-nonce",
                                    (await getUserNonce())[
                                        "webform-file-upload-nonce"
                                    ]
                                );
                                imageUploadForm.append(
                                    "file_prefix",
                                    filePrefix
                                );

                                return API.uploadWebformFile(
                                    imageUploadForm
                                ).then(response => {
                                    if (response.annotation_id) {
                                        // set annotation id back in corresponding webform text input
                                        const fileFieldAttribute =
                                            fileField.getAttribute(
                                                "data-file-field"
                                            );
                                        const realInput = fileFieldAttribute
                                            ? document.getElementsByName(
                                                  fileFieldAttribute
                                              )
                                            : [];

                                        if (
                                            realInput[0] &&
                                            realInput[0] instanceof
                                                HTMLInputElement
                                        ) {
                                            realInput[0].value =
                                                response.annotation_id;
                                        }
                                    }
                                });
                            }
                        }
                    })
                );
            }
        }
    };

    React.useEffect(() => {
        if (scriptRef.current) {
            // set embed html
            scriptRef.current.innerHTML = props.embedded_code;

            window.MsCrmMkt = null;

            // execute script tags
            executeScriptElements(scriptRef.current);

            // check interval to make sure dynamics form loader api is available
            const checkFormLoadedInterval = setInterval(() => {
                if (window.MsCrmMkt && window.MsCrmMkt.MsCrmFormLoader) {
                    clearInterval(checkFormLoadedInterval);

                    // on form load, bind form inputs
                    window.MsCrmMkt.MsCrmFormLoader.on(
                        "afterFormLoad",
                        handleFormLoaded
                    );

                    // form submit event
                    window.MsCrmMkt.MsCrmFormLoader.on(
                        "formSubmit",
                        handleFormSubmit
                    );
                }
            }, 100);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.embedded_code]);

    return (
        <div className="marketing-form">
            <SectionHeader
                sectionName="marketing-form"
                title={props.title}
                description={props.description}
            />

            <div className="marketing-form__form" ref={scriptRef}></div>

            <ServerErrorMessage>{state.error}</ServerErrorMessage>

            <Loader
                fixed={true}
                active={state.loading}
                text={state.loadingMessage}
            />
        </div>
    );
};

export default MarketingForm;
